import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import {
  AuthAccessTokenDetails,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  LoginOAuthResponse,
  SwapAuthForAccessTokens
} from '../../models/login';
import {
  isUserNamePasswordRequired,
  returnTransformTokenDetails
} from '../../utils/login/initiate-login-utils';
import { InitiateLoginFacadeExtendedHelperService } from './initiate-login-facade-extended-helper.service';

@Injectable({
  providedIn: 'root'
})
export class InitiateLoginFacadeHelperService extends InitiateLoginFacadeExtendedHelperService {
  processResponse(
    response:
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
      | AuthAccessTokenDetails
      | string
      | boolean,
    loginOAuthParams: LoginOAuthParams,
    appToken = false
  ): Observable<boolean | AuthAccessTokenDetails | never> {
    if (typeof response === 'boolean') {
      return of(response);
    } else if (typeof response === 'string') {
      return throwError(response);
    } else {
      return this.processResponseHelper(response, loginOAuthParams, appToken);
    }
  }

  processResponseHelper(
    response:
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
      | AuthAccessTokenDetails,
    loginOAuthParams: LoginOAuthParams,
    appToken = false
  ): Observable<boolean | AuthAccessTokenDetails | never> {
    if (isUserNamePasswordRequired(response, loginOAuthParams)) {
      this.retryCount = 0;
      return of(true);
    } else if ('access_token' in response) {
      if (!appToken) {
        this.loginRememberMeUtils.sendTokenServiceWorker(
          loginOAuthParams.oauthConfig,
          response
        );
      }
      this.retryCount = 0;
      return returnTransformTokenDetails(
        response,
        this.postWithCredentialsFacade
      );
    } else {
      return throwError(response);
    }
  }

  private isUserRememberedStatus(
    response: LoginOAuthResponse,
    oauthConfig: LoginOAuthParams['oauthConfig']
  ): boolean {
    return response.status === oauthConfig.IS_USER_REMEMBERED;
  }

  private isCompletedStatus(
    response: LoginOAuthResponse,
    oauthConfig: LoginOAuthParams['oauthConfig']
  ): boolean {
    return response.status === oauthConfig.COMPLETED;
  }

  private isPasswordRequiredStatus(
    response: LoginOAuthResponse,
    oauthConfig: LoginOAuthParams['oauthConfig']
  ): boolean {
    return response.status === oauthConfig.USERNAME_PASSWORD_REQUIRED;
  }

  private getResponseId(response: LoginOAuthResponse): string {
    return response?.id || '';
  }

  handleAuthorizationResponse(
    response: LoginOAuthResponse,
    loginOAuthParams: LoginOAuthParams
  ): Observable<LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens> {
    if (this.isUserRememberedStatus(response, loginOAuthParams.oauthConfig)) {
      return this.loginPostWithFingerPrint.loginPostWithFingerPrint(
        loginOAuthParams.oauthConfig,
        this.getResponseId(response)
      );
    } else if (this.isCompletedStatus(response, loginOAuthParams.oauthConfig)) {
      return this.swapAccessToken.swapAuthCodeForToken(
        loginOAuthParams,
        response.authorizeResponse?.code
      );
    } else {
      return throwError(response);
    }
  }
}
